import React, { useState, useRef, useEffect } from 'react';
import { FilterIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, WindowsIcon, AndroidIcon, AppleIcon } from '../icons';

const ItemsFilter = ({ 
  sortValue, 
  platformValue,
  onSortChange, 
  onPlatformChange,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const sortOptions = [
    { value: 'newest-first', label: 'Newest first' },
    { value: 'oldest-first', label: 'Oldest first' }
  ];

  const platformOptions = [
    { value: 'all', label: 'All' },
    { value: 'windows', label: 'Windows', icon: WindowsIcon, iconColor: '#1DA4F3' },
    { value: 'android', label: 'Android', icon: AndroidIcon, iconColor: '#56B93E' },
    { value: 'ios-macos', label: 'IOS/MacOS', icon: AppleIcon }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSortSelect = (option) => {
    onSortChange(option);
    setIsOpen(false);
  };

  const handlePlatformSelect = (option) => {
    onPlatformChange(option);
    setIsOpen(false);
  };

  const selectedSortOption = sortOptions.find(option => option.value === sortValue);

  return (
    <div className={`relative ${className}`}>
      {/* Main Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between h-[38px] rounded-xl transition-colors hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A]"
      >
        {/* Content Container */}
        <div className="flex items-center" style={{ height: '32px' }}>
          {/* Filter Icon Container */}
          <div 
            className="w-8 h-8 flex items-center justify-center"
          >
            <FilterIcon 
              size={12} 
              className="text-[#7B7B7B] dark:text-[#848484]"
            />
          </div>
          
          {/* Text */}
          <span 
            className="font-medium text-[#121212] dark:text-[#D0D0D0]"
            style={{
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '-0.1px',
              marginRight: '8px'
            }}
          >
            {selectedSortOption?.label || 'All items'}
          </span>

          {/* Separator Line */}
          <div 
            className="w-px h-3 bg-[#ECECEC] dark:bg-[#202020] mr-2"
          />

          {/* Selected Platform Display */}
          {(() => {
            const selectedPlatformOption = platformOptions.find(option => option.value === platformValue);
            if (platformValue === 'all') {
              return (
                <span 
                  className="font-medium text-[#121212] dark:text-[#D0D0D0]"
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '16px',
                    letterSpacing: '-0.1px',
                    marginRight: '8px'
                  }}
                >
                  All
                </span>
              );
            } else {
              const IconComponent = selectedPlatformOption?.icon;
              return IconComponent ? (
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <IconComponent 
                    size={12} 
                    className="text-[#121212] dark:text-[#D0D0D0]"
                    color={selectedPlatformOption.iconColor ? selectedPlatformOption.iconColor : undefined}
                  />
                </div>
              ) : null;
            }
          })()}
        </div>

        {/* Chevron Icon */}
        <div className="flex items-center justify-center pr-2">
          {isOpen ? (
            <ChevronUpIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
          ) : (
            <ChevronDownIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
          )}
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-lg shadow-lg z-50"
        >
          {/* Sort Options Section */}
          <div className="p-2 space-y-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSortSelect(option)}
                className={`w-full flex items-center hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-colors ${
                  option.value === sortValue ? 'bg-[#F7F7F7] dark:bg-[#252525]' : ''
                }`}
                style={{
                  height: '32px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  borderRadius: '8px'
                }}
              >
                {/* Icon - Show checkmark for selected, empty space for others */}
                <div className="w-3 h-3 flex items-center justify-center" style={{ marginRight: '8px' }}>
                  {option.value === sortValue ? (
                    <CheckIcon 
                      size={12} 
                      className="text-[#121212] dark:text-[#D0D0D0]" 
                    />
                  ) : null}
                </div>
                
                {/* Text */}
                <span 
                  className="font-medium text-[#121212] dark:text-[#D0D0D0]"
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '16px',
                    letterSpacing: '-0.1px'
                  }}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>

          {/* Divider Line */}
          <div 
            className="w-full h-px bg-[#ECECEC] dark:bg-[#202020] mx-2"
          />

          {/* Platform Options Section */}
          <div className="p-2 space-y-1">
            {platformOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handlePlatformSelect(option)}
                  className={`w-full flex items-center hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-colors ${
                    option.value === platformValue ? 'bg-[#F7F7F7] dark:bg-[#252525]' : ''
                  }`}
                  style={{
                    height: '32px',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    borderRadius: '8px'
                  }}
                >
                  {/* Icon - Show checkmark for "All", platform icon for others */}
                  <div className="w-3 h-3 flex items-center justify-center mr-2">
                    {option.value === 'all' ? (
                      option.value === platformValue ? (
                        <CheckIcon 
                          size={12} 
                          className="text-[#121212] dark:text-[#D0D0D0]" 
                        />
                      ) : null
                    ) : (
                      IconComponent && (
                        <div style={{ opacity: option.value === platformValue ? 1 : 0.2 }}>
                          <IconComponent 
                            size={12} 
                            className="text-[#121212] dark:text-[#D0D0D0]"
                            color={option.iconColor ? option.iconColor : undefined}
                          />
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Text */}
                  <span 
                    className="font-medium text-[#121212] dark:text-[#D0D0D0]"
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '16px',
                      letterSpacing: '-0.1px'
                    }}
                  >
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsFilter;
